import { useNavigate } from 'react-router-dom';
import { CSSProperties, ReactNode } from 'react';
import { BrandGlyph, MODULE_COLOR, BRAND_VERSION } from '../lib/branding';
import { AuthBadge } from '../components/AuthBadge';
import { safeTopPad, safeBottomPad } from '../lib/safeArea';
import { AngleIcon, HeightIcon, TorsionIcon } from '../components/ModuleIcons';

interface Tile {
  to: string;
  title: string;
  subtitle: string;
  badge?: string;
  accent: string;
  /** Pre-rendered SVG icon. Inline SVG (not Unicode characters) so the
   *  glyph is identical on every iPhone — WKWebView's font fallback
   *  doesn't include the Mathematical-Symbols / Supplemental-Arrows
   *  ranges (∠ and ⟳ rendered as "?" before this fix). */
  icon: ReactNode;
}

// Visual size of the icon glyph inside the 60×60 accent box on HomePage.
const TILE_ICON_PX = 32;

const TILES: Tile[] = [
  {
    to: '/app/angle',
    title: 'Osteotomy Angle',
    subtitle: 'Two-line angle from a fluoroscopy frame.',
    accent: MODULE_COLOR.angle,
    icon: <AngleIcon width={TILE_ICON_PX} height={TILE_ICON_PX} />,
  },
  {
    to: '/app/height',
    title: 'Osteotomy Height',
    subtitle: 'Distance with optional mm calibration.',
    accent: MODULE_COLOR.height,
    icon: <HeightIcon width={TILE_ICON_PX} height={TILE_ICON_PX} />,
  },
  {
    to: '/app/torsion',
    title: 'Torsion',
    subtitle: 'Live goniometer · capture-and-document workflow.',
    accent: MODULE_COLOR.torsion,
    icon: <TorsionIcon width={TILE_ICON_PX} height={TILE_ICON_PX} />,
  },
];

export default function HomePage() {
  const nav = useNavigate();

  return (
    <main style={mainStyle}>
      {/* Cinematic background — subtle radial wash + bottom haze */}
      <div style={atmosphereStyle} aria-hidden />

      <div style={contentStyle}>
        <header style={headerStyle}>
          <div style={brandRowStyle}>
            <BrandGlyph size={36} color={MODULE_COLOR.angle} />
            <div
              style={{
                fontSize: 13,
                letterSpacing: 1.4,
                textTransform: 'uppercase',
                color: 'var(--text-dim)',
                fontWeight: 600,
                flex: 1,
              }}
            >
              CADtomie
            </div>
            <AuthBadge />
          </div>

          <h1 style={wordmarkStyle}>Intraop</h1>
          <p style={taglineStyle}>
            Fast intraoperative measurements. Local processing. No upload.
          </p>
        </header>

        <div style={tilesContainerStyle}>
          {TILES.map((t) => (
            <button
              key={t.to}
              type="button"
              onClick={() => nav(t.to)}
              style={tileStyle(t.accent)}
              onTouchStart={(e) => {
                (e.currentTarget as HTMLElement).style.transform = 'scale(0.985)';
              }}
              onTouchEnd={(e) => {
                (e.currentTarget as HTMLElement).style.transform = 'scale(1)';
              }}
            >
              <div style={tileGlowStyle(t.accent)} aria-hidden />
              <div style={iconBoxStyle(t.accent)}>{t.icon}</div>
              <div style={tileBodyStyle}>
                <div style={tileTitleRowStyle}>
                  <span style={{ fontSize: 19, fontWeight: 600, letterSpacing: -0.2 }}>
                    {t.title}
                  </span>
                  {t.badge && (
                    <span style={badgeStyle(t.accent)}>{t.badge}</span>
                  )}
                </div>
                <div style={tileSubtitleStyle}>{t.subtitle}</div>
              </div>
              <div style={chevronStyle}>›</div>
            </button>
          ))}
        </div>

        <footer style={footerStyle}>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', letterSpacing: 0.3 }}>
            For research and intraoperative assistance only.
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', letterSpacing: 0.3 }}>
            v{BRAND_VERSION}
          </div>
        </footer>
      </div>
    </main>
  );
}

/* ═══════════ styles ═══════════ */

const mainStyle: CSSProperties = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
  minHeight: '100dvh',
  background: '#000',
  overflow: 'hidden',
};

const atmosphereStyle: CSSProperties = {
  position: 'absolute',
  inset: 0,
  background: `
    radial-gradient(circle at 15% -10%, rgba(124,196,255,0.12), transparent 45%),
    radial-gradient(circle at 95% 110%, rgba(255,159,10,0.08), transparent 50%),
    linear-gradient(180deg, #050507 0%, #000 60%, #000 100%)
  `,
  pointerEvents: 'none',
};

const contentStyle: CSSProperties = {
  position: 'relative',
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  padding: '0 22px',
  zIndex: 1,
};

const headerStyle: CSSProperties = {
  // safe-area inset OR 20 px, whichever is larger — keeps the brand row
  // below the Dynamic Island in standalone PWA mode.
  paddingTop: `calc(${safeTopPad(20)})`,
  paddingBottom: 36,
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
};

const brandRowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  marginBottom: 12,
};

const wordmarkStyle: CSSProperties = {
  margin: 0,
  fontSize: 44,
  fontWeight: 700,
  letterSpacing: -1.2,
  lineHeight: 1,
  background:
    'linear-gradient(180deg, rgba(255,255,255,1) 0%, rgba(255,255,255,0.78) 100%)',
  WebkitBackgroundClip: 'text',
  backgroundClip: 'text',
  color: 'transparent',
};

const taglineStyle: CSSProperties = {
  margin: '8px 0 0',
  color: 'var(--text-dim)',
  fontSize: 15,
  letterSpacing: -0.1,
  maxWidth: 320,
};

const tilesContainerStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 14,
};

function tileStyle(accent: string): CSSProperties {
  return {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    width: '100%',
    minHeight: 96,
    padding: '16px 18px',
    borderRadius: 22,
    background:
      'linear-gradient(180deg, rgba(28,28,32,0.92) 0%, rgba(20,20,24,0.92) 100%)',
    border: '1px solid rgba(255,255,255,0.06)',
    boxShadow:
      '0 1px 0 rgba(255,255,255,0.04) inset, 0 8px 28px rgba(0,0,0,0.55)',
    color: 'var(--text)',
    transition: 'transform 100ms ease',
    backdropFilter: 'blur(20px)',
    WebkitBackdropFilter: 'blur(20px)',
    boxSizing: 'border-box',
    overflow: 'hidden',
    textAlign: 'left',
    // Subtle module-coloured edge on the left
    borderLeft: `1px solid ${accent}55`,
  };
}

function tileGlowStyle(accent: string): CSSProperties {
  return {
    position: 'absolute',
    left: -20,
    top: -20,
    width: 120,
    height: 120,
    borderRadius: '50%',
    background: `radial-gradient(circle, ${accent}26 0%, transparent 70%)`,
    pointerEvents: 'none',
  };
}

function iconBoxStyle(accent: string): CSSProperties {
  return {
    position: 'relative',
    width: 60,
    height: 60,
    borderRadius: 16,
    background: `linear-gradient(135deg, ${accent}26, ${accent}10)`,
    border: `1px solid ${accent}3d`,
    color: accent,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  };
}

const tileBodyStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  flex: 1,
  minWidth: 0,
};

const tileTitleRowStyle: CSSProperties = {
  display: 'flex',
  gap: 8,
  alignItems: 'center',
  flexWrap: 'wrap',
};

function badgeStyle(accent: string): CSSProperties {
  return {
    fontSize: 10,
    fontWeight: 700,
    color: accent,
    background: `${accent}22`,
    border: `1px solid ${accent}3d`,
    padding: '2px 8px',
    borderRadius: 999,
    letterSpacing: 0.5,
    textTransform: 'uppercase',
  };
}

const tileSubtitleStyle: CSSProperties = {
  color: 'var(--text-dim)',
  fontSize: 13.5,
  letterSpacing: -0.05,
};

const chevronStyle: CSSProperties = {
  color: 'var(--text-muted)',
  fontSize: 24,
  fontWeight: 300,
  marginLeft: 4,
};

const footerStyle: CSSProperties = {
  marginTop: 'auto',
  paddingTop: 24,
  paddingLeft: 4,
  paddingRight: 4,
  // Safe-area-aware bottom padding so the footer clears the iOS home indicator.
  paddingBottom: safeBottomPad(12),
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  fontVariantNumeric: 'tabular-nums',
};
